import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff } from '../utils/icons';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import mascot from '../assets/placeholder.png';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Mock login - in a real app, this would call an API
    setTimeout(() => {
      // Save user login state to localStorage
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('user', JSON.stringify({ name: 'Bill' }));
      
      setIsLoading(false);
      // Navigate to home page instead of dashboard
      navigate('/');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col sm:flex-row">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text mb-2">
              MATRIX FINANCE
            </h1>
            <p className="text-text/70">Your journey to financial freedom starts here</p>
          </div>

          <div className="glass-card p-8">
            <h2 className="text-2xl font-semibold mb-6">Sign In</h2>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                leftIcon={<FiMail size={18} />}
              />
              
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                leftIcon={<FiLock size={18} />}
                rightIcon={
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="focus:outline-none"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                }
              />
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-primary border-border rounded bg-card"
                  />
                  <label htmlFor="remember-me" className="ml-2 text-text/70">
                    Remember me
                  </label>
                </div>
                
                <Link to="/forgot-password" className="text-primary hover:text-primary/80">
                  Forgot password?
                </Link>
              </div>
              
              <Button type="submit" fullWidth isLoading={isLoading}>
                Sign In
              </Button>
            </form>
            
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-text/50">Or continue with</span>
                </div>
              </div>
              
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="w-full py-2.5 px-4 border border-border rounded-lg font-medium text-sm text-text hover:bg-card-hover focus:outline-none focus:ring-2 focus:ring-primary/50 flex items-center justify-center"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                    <path fill="none" d="M1 1h22v22H1z" />
                  </svg>
                  Google
                </button>
                
                <button
                  type="button"
                  className="w-full py-2.5 px-4 border border-border rounded-lg font-medium text-sm text-text hover:bg-card-hover focus:outline-none focus:ring-2 focus:ring-primary/50 flex items-center justify-center"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
                    />
                  </svg>
                  Facebook
                </button>
              </div>
            </div>
            
            <p className="mt-8 text-center text-sm text-text/70">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:text-primary/80 font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      {/* Right side - Image/Design */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-background via-background to-foreground overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center max-w-lg">
            <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-1">
              <div className="w-full h-full rounded-full bg-background flex items-center justify-center">
                <img src={mascot} alt="Matrix Finance Mascot" className="w-16 h-16 object-contain" />
              </div>
            </div>
            
            <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
              Hey! I'm your finance Guide!
            </h2>
            
            <p className="text-text mb-8 leading-relaxed">
              Learn, Chat, and Connect with Experts. Your journey to financial freedom starts here.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center glass-card p-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary mr-4">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 20H7C4.79086 20 3 18.2091 3 16V8C3 5.79086 4.79086 4 7 4H17C19.2091 4 21 5.79086 21 8V16C21 18.2091 19.2091 20 17 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 8L10.5 13L21 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium">Chat with Financial Experts</h3>
                  <p className="text-sm text-text/70">Get answers to all your financial questions</p>
                </div>
              </div>
              
              <div className="flex items-center glass-card p-4">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center text-secondary mr-4">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium">Personalized Finance Plans</h3>
                  <p className="text-sm text-text/70">Custom strategies tailored for your needs</p>
                </div>
              </div>
              
              <div className="flex items-center glass-card p-4">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center text-accent mr-4">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 6V12M16 16V16.01M8 18V12M8 8V7.99M12 10V16M12 20V20.01M12 4V4.01M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium">Real-time Market Insights</h3>
                  <p className="text-sm text-text/70">Stay updated with latest financial trends</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Animated shapes */}
        <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-primary/20 blur-3xl animate-pulse-slow"></div>
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-accent/20 blur-3xl animate-pulse-slow"></div>
      </div>
    </div>
  );
};

export default Login; 